import { isNotDefined } from '@sgme/fp';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';

import type { MarginConverter } from '@/App/tiles/shared/CommercialMargin/MarginSection.tsx';

import type { BidAskPair } from './types.ts';

/**
 * Configuration for the margin field editor hook
 */
type UseMarginFieldEditorConfig = {
  /**
   * Current value to edit
   */
  value: BidAskPair;

  /**
   * Optional converter to transform values
   */
  converter?: MarginConverter;

  /**
   * Callback called when saving a new value
   */
  onSave: (value: BidAskPair) => void;
};

/**
 * Result of the margin field editor hook
 */
type UseMarginFieldEditorResult = {
  /**
   * Indicates if the field is being edited
   */
  isFocused: boolean;

  /**
   * Indicates if the edited value is invalid
   */
  isInvalid: boolean;

  /**
   * Edited value (string)
   */
  editedValue: string;

  /**
   * Reference to the input for focus
   */
  inputRef: React.RefObject<HTMLInputElement | null>;

  /**
   * Starts editing
   */
  startEditing: () => void;

  /**
   * Handles value change in the input
   */
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;

  /**
   * Saves the edited value
   */
  saveValue: () => void;
};

/**
 * Hook to manage editing of a commercial margin field
 * Handles focus, validation, and saving of bid/ask values
 */
export function useMarginFieldEditor(config: UseMarginFieldEditorConfig): UseMarginFieldEditorResult {
  const { value, converter, onSave } = config;

  const inputRef = useRef<HTMLInputElement>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [editedValue, setEditedValue] = useState('');

  // Automatic focus when entering edit mode
  useEffect(() => {
    if (!isFocused) {
      return;
    }

    inputRef.current?.focus();
  }, [isFocused]);

  const startEditing = () => {
    const displayValue = converter ? converter.toDisplay(value) : value;
    const stringValue = converter ? converter.toString(displayValue) : `${displayValue.bid}/${displayValue.ask}`;

    setEditedValue(stringValue);
    setIsFocused(true);
    setIsInvalid(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEditedValue(event.target.value);
    setIsInvalid(false);
  };

  const saveValue = () => {
    let parsedValue: BidAskPair | undefined;

    // Normalize: if no '/' present, treat single value as "value/value"
    const normalizedValue = editedValue.includes('/')
      ? editedValue
      : editedValue === ''
        ? '0/0'
        : `${editedValue}/${editedValue}`;

    if (converter) {
      parsedValue = converter.fromString(normalizedValue);
    } else {
      // Default parser: "bid/ask"
      const [bid, ask] = normalizedValue.split('/').map((v) => Number.parseFloat(v));

      if (!isNotDefined(bid) && !Number.isNaN(bid) && !isNotDefined(ask) && !Number.isNaN(ask)) {
        parsedValue = { bid, ask };
      }
    }

    if (isNotDefined(parsedValue)) {
      // Invalid value: restore previous value
      const displayValue = converter ? converter.toDisplay(value) : value;
      const stringValue = converter ? converter.toString(displayValue) : `${displayValue.bid}/${displayValue.ask}`;

      setEditedValue(stringValue);
      setIsInvalid(true);
      return;
    }

    // Convert display value to internal value
    const internalValue = converter ? converter.fromDisplay(parsedValue) : parsedValue;

    onSave(internalValue);

    setEditedValue('');
    setIsFocused(false);
    setIsInvalid(false);
  };

  return {
    isFocused,
    isInvalid,
    editedValue,
    inputRef,
    startEditing,
    handleChange,
    saveValue,
  };
}
